import HTTP from '@/utils/http'
import notification from '@/utils/notification'
import i18n from '@/utils/i18n'
import { tenderStatusAfterIssue, tenderStatus } from '@/utils/locale'
import { maxAttachmentDescriptionLength } from '@/utils/consts'
import { getTermIdByTenderId } from '@/api/tenders/request'
import { markTenantProjectsDashboardListStale } from '@/store/dashboardListStale'

export const UPLOAD_IMAGE_URL = '/docs/image'

export async function uploadImage (data) {
  try {
    const response = await HTTP.post(UPLOAD_IMAGE_URL, data)
    return response.data.data
  } catch (err) {
    notification.handleResponseError(err, 'attachmentDescription')
    throw err
  }
}

export async function getAttachments (id, effectiveOnly = false, sortType, isSilentGet) {
  try {
    const get = isSilentGet ? HTTP.silentGet : HTTP.get
    const response = await get('/docs/', {
      params: { src: id, effectiveOnly, sortType },
    })
    return (response && response.data) ? response.data.data : []
  } catch (error) {
    console.error('getAttachments error', error)
    return []
  }
}

function getDescription (file) {
  return Object.prototype.hasOwnProperty.call(file, '__descriptionDraft')
    ? file.__descriptionDraft
    : file.description
}

function validateDescriptionLength (description) {
  if (description && description.length > maxAttachmentDescriptionLength) {
    const message = i18n.t('hint.doNotOverXChars', [maxAttachmentDescriptionLength])
    notification.fireNotification(false, message)
    throw new Error(message)
  }
}

export async function updateDescription (file, additionalAttrs) {
  const description = getDescription(file)
  validateDescriptionLength(description)

  try {
    const response = await HTTP.put(`/docs/${file.id}`,
      { description, ...additionalAttrs })
    return response.data.data
  } catch (err) {
    notification.handleResponseError(err, 'attachmentDescription')
    throw err
  }
}

export async function updateEffectiveDate (file) {
  try {
    const response = await HTTP.put(`/docs/${file.id}`,
      { effectiveDate: file.effectiveDate })
    return response.data.data
  } catch (err) {
    notification.handleResponseError(err, 'attachmentEffectiveDate')
    throw err
  }
}

export async function updateEndDate (file) {
  try {
    const response = await HTTP.put(`/docs/${file.id}`,
      { endDate: file.endDate })
    return response.data.data
  } catch (err) {
    notification.handleResponseError(err, 'attachmentEndDate')
    throw err
  }
}
export async function saveAttachment (attachment) {
  await Promise.all(
    attachment.files.map(async (file, index) => {
      const description = getDescription(file)
      validateDescriptionLength(description)
      file.order = index
      await updateOrder(file.id, index)
      if (description) {
        file.description = description
        await updateDescription(file)
      }
      return file
    }),
  )
}

export async function updateDocuments (files, action, projectId) {
  try {
    const response = await HTTP.put('/docs', {files, projectId}, { headers: { 'X-CMS-Action': action } })
    if (projectId) {
      markTenantProjectsDashboardListStale()
    }
    return response.data.data
  } catch (err) {
    notification.handleResponseError(err, 'updateDocuments')
    throw err
  }
}

export async function updateOrder (fileId, order) {
  try {
    const response = await HTTP.put(`/docs/${fileId}`,
      { order })
    return response.data.data
  } catch (err) {
    notification.handleResponseError(err, 'attachmentDescription')
    throw err
  }
}

export async function deleteDocument (id, headers, { markTenantProjectsStale = false } = {}) {
  try {
    if (headers) {
      await HTTP.delete(`/docs/${id}`, { headers })
    } else {
      await HTTP.delete(`/docs/${id}`)
    }
    if (markTenantProjectsStale) {
      markTenantProjectsDashboardListStale()
    }
    return true
  } catch (err) {
    notification.handleResponseError(err, 'Delete File')
    return false
  }
}
export async function deleteAllDocuments (id) {
  try {
    await HTTP.delete(`/docs/?src=${id}`)
  } catch (err) {
    throw err
  }
}

export async function getTermsConditions ({ isContractorView, tenderId, status, isTender, isAddendum }) {
  let files = []
  try {
    if (tenderId && (isContractorView || isAddendum || Object.keys(tenderStatusAfterIssue).includes(status) || status === tenderStatus.ISSUED)) {
      files = await getTenderTncDocs(tenderId)
    } else {
      const responseData = await getTermIdByTenderId(tenderId)
      const termsId = isTender ? responseData.tenderTermsId : responseData.itqTermsId
      files = await getAttachments(termsId, true)
    }
  } catch (err) {
    notification.handleResponseError(err, 'Terms and Condtions')
  }
  return files
}

export async function getTenderTncDocs (tenderId) {
  try {
    const response = await HTTP.get(`/tenders/${tenderId}/tnc-docs`)
    return (response && response.data) ? response.data.data : []
  } catch (err) {
    notification.handleResponseError(err)
    throw err
  }
}

export async function getDoc (id, query) {
  try {
    let { data: img } = await HTTP.get(`docs/${id}`, { params: query, responseType: 'blob' })
    let blob = new Blob([img], { type: img.type })
    return URL.createObjectURL(blob)
  } catch (error) {
    throw error
  }
}
